import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, UserCheck, X } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { UserPicker } from "@/components/UserPicker";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import type { ImpersonationSession } from "@tilt/admin-sdk";

/**
 * Impersonation console - lets a super_admin start a session against
 * any user UUID in their subtree + see history.
 *
 * UI-level guards:
 *   - Single "you already have an active session" card above the form
 *     when a session is active (API also enforces 409 but we avoid the
 *     round-trip).
 *   - TTL selector bounded at 15min..4h - the DB CHECK is the 4h
 *     ceiling; the 15/30/60min shorter options are UI-level sensible
 *     defaults.
 *
 * Picking a user by id (rather than a proper search) is deliberate for
 * this first pass - the Users page can later deep-link here with
 * target_user_id pre-filled once the Users CRUD lands.
 */
export function ImpersonatePage(): JSX.Element {
  usePageTitle("Impersonate");
  // Super-admin gating lives in the route wrapper (RequireSuperAdmin)
  // + sidebar visibility predicate - both consult the acting-as
  // profile so an impersonated partner_admin is correctly redirected
  // to /dashboard. No in-component guard needed here; adding one
  // would violate rules-of-hooks (the useQuery / useMutation calls
  // below must always run in the same order).
  const queryClient = useQueryClient();

  const current = useQuery({
    queryKey: ["impersonation", "current"],
    queryFn: () => apiClient.getCurrentImpersonation(),
    retry: false,
  });

  const history = useQuery({
    queryKey: ["impersonation", "list"],
    queryFn: () => apiClient.listImpersonations(),
    retry: false,
  });

  // Preselect the target from ``?target=<user_id>`` when the admin
  // deep-linked here from the Users page row button. The UserPicker
  // renders the resolved email as soon as it gets an id, so the
  // admin sees who they're about to impersonate without re-picking.
  const [searchParams] = useSearchParams();
  const initialTargetId = searchParams.get("target") ?? "";
  const [targetUserId, setTargetUserId] = useState(initialTargetId);
  const [reason, setReason] = useState("");
  const [ttlMinutes, setTtlMinutes] = useState(60);
  const [error, setError] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () =>
      apiClient.startImpersonation({
        target_user_id: targetUserId,
        reason,
        ttl_seconds: ttlMinutes * 60,
      }),
    onSuccess: () => {
      setError(null);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["impersonation"] });
      // Drop the acting-as cache so sidebar / gates re-fetch and
      // swap to the target's role view immediately. Without this
      // the admin would need to reload to see the perspective swap
      // and the "which features does this user have?" preview
      // would be wrong until the query's staleTime (60s) elapsed.
      void queryClient.invalidateQueries({ queryKey: ["profile", "acting-as"] });
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const isActive = current.data?.session != null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Impersonation</h1>
        <p className="text-sm text-muted-foreground">
          Start a time-bound session to view the portal as another user. Every action under this
          session is audited to your account.
        </p>
      </div>

      {isActive && current.data?.session ? (
        <ActiveSessionCard session={current.data.session} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate();
          }}
          className="space-y-4 rounded-lg border bg-card p-4 shadow-sm sm:p-6"
        >
          <div>
            <label
              htmlFor="target-user-id"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Target user
            </label>
            <UserPicker
              id="target-user-id"
              required
              value={targetUserId}
              onChange={setTargetUserId}
              placeholder="Type an email to pick a user"
            />
          </div>
          <div>
            <label
              htmlFor="reason"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Reason <span className="text-muted-foreground/80">(required for audit)</span>
            </label>
            <textarea
              id="reason"
              required
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. debugging payment failure #123 reported by dealership X"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="ttl" className="mb-1 block text-xs font-medium text-muted-foreground">
              Duration
            </label>
            <select
              id="ttl"
              value={ttlMinutes}
              onChange={(e) => setTtlMinutes(Number(e.target.value))}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={240}>4 hours (max)</option>
            </select>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <button
            type="submit"
            disabled={start.isPending || !targetUserId || !reason.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 sm:w-auto"
          >
            <UserCheck className="h-4 w-4" />
            {start.isPending ? "Starting…" : "Start impersonation"}
          </button>
        </form>
      )}

      <section>
        <h2 className="mb-3 text-sm font-semibold">Recent sessions</h2>
        {history.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {history.isError && (
          <p className="text-sm text-destructive">
            {history.error instanceof Error ? history.error.message : "Failed to load history"}
          </p>
        )}
        {history.data && history.data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">No sessions yet.</p>
        )}
        {history.data && history.data.items.length > 0 && (
          <ul className="space-y-2">
            {history.data.items.map((s) => (
              <SessionRow key={s.id} session={s} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ActiveSessionCard(props: { session: ImpersonationSession }): JSX.Element {
  const { session } = props;
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Clock className="h-4 w-4 text-primary" />
        <span>Active session</span>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        You're currently impersonating <span className="font-medium">{session.target.email}</span>.
        Stop the session using the banner at the top of the page before starting another one.
      </p>
      <dl className="mt-4 grid grid-cols-1 gap-2 text-xs sm:grid-cols-[100px_1fr]">
        <dt className="text-muted-foreground">started</dt>
        <dd className="font-mono">{new Date(session.started_at).toLocaleString()}</dd>
        <dt className="text-muted-foreground">expires</dt>
        <dd className="font-mono">{new Date(session.expires_at).toLocaleString()}</dd>
        <dt className="text-muted-foreground">reason</dt>
        <dd className="break-words">{session.reason}</dd>
      </dl>
    </div>
  );
}

function SessionRow(props: { session: ImpersonationSession }): JSX.Element {
  const { session } = props;
  const isActive = session.ended_at === null;
  return (
    <li className="rounded-md border bg-background p-3 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{session.actor.email}</span>
        <span className="text-muted-foreground">→</span>
        <span className="break-words font-medium">{session.target.email}</span>
        {isActive ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
            active
          </span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            <X className="h-3 w-3" />
            {session.ended_reason}
          </span>
        )}
      </div>
      <div className="mt-1 break-words text-muted-foreground">{session.reason}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-muted-foreground">
        <span>started {new Date(session.started_at).toLocaleString()}</span>
        {session.ended_at && <span>ended {new Date(session.ended_at).toLocaleString()}</span>}
      </div>
    </li>
  );
}
